import React, { useEffect, useState } from 'react';

import { AdaptiveCarousel, AdaptiveCarouselSlide, Box, Visible } from '@vp/swan';
import { useLocalizations } from '@vp/ubik-localizations-provider';

import { Image } from '../domain/Image';
import { Slide } from '../domain/Slide';
import { SlideArray } from '../domain/SlideArray';
import { Video } from '../domain/Video';
import { GalleryId } from '../libs/constants';
import { pauseVideo } from '../libs/videoActions';

import { GalleryImage } from './GalleryImage';
import { GalleryVideo } from './GalleryVideo';
import { Thumbnails } from './Thumbnails';
import { ZoomDialog } from './zoom/ZoomDialog';
import { ZoomedDisplayData } from './zoom/ZoomedDisplayData';

export interface GoToSlide {
  slideNumber: number;
}
interface GalleryProps {
  mediaArray: SlideArray;
  goToSlide?: GoToSlide;
  zoomContainerId?: string;
  tenant: string;
  canonicalDomain: string;
  mpvId: string;
  productKey: string;
}
export const Gallery: React.FC<GalleryProps> = (props) => {
  const { t } = useLocalizations();
  const [zoomData, setZoomData] = useState<ZoomedDisplayData>(new ZoomedDisplayData());
  const [currentSlide, setCurrentSlide] = useState<number>(
    props.goToSlide ? props.goToSlide.slideNumber : 1
  );
  const [key, setKey] = useState<number>(1);

  const displayZoomDialog = (slide: Slide): void => {
    if (props.zoomContainerId && slide.isImage) {
      setZoomData(new ZoomedDisplayData((slide.media as Image).zoom, slide.getAlt(t), true));
    }
  };

  const handleSlideChange = (firstSlide: number): void => {
    const unselectedSlide = props.mediaArray.getSlideNumber(currentSlide);
    if (unselectedSlide?.isVideo) {
      pauseVideo(unselectedSlide.galleryId);
    }
    setCurrentSlide(firstSlide);
  };

  const renderCarouselSlides = (withZoom: boolean = false): React.ReactNode[] => {
    return props.mediaArray.all.map((slide: Slide) => {
      if (slide.isVideo) {
        return (
          <AdaptiveCarouselSlide key={slide.galleryId} alignSelf="stretch">
            <GalleryVideo
              video={slide.media as Video}
              playerId={slide.galleryId}
              tenant={props.tenant}
              canonicalDomain={props.canonicalDomain}
            />
          </AdaptiveCarouselSlide>
        );
      }
      return (
        <AdaptiveCarouselSlide
          key={slide.galleryId}
          onClick={withZoom ? () => displayZoomDialog(slide) : undefined}
        >
          <GalleryImage
            image={slide.media as Image}
            alt={slide.getAlt(t)}
            zoomContainerId={props.zoomContainerId}
          />
        </AdaptiveCarouselSlide>
      );
    });
  };

  useEffect(() => {
    if (props.goToSlide && props.goToSlide.slideNumber !== currentSlide) {
      forceRerender();
    }

    function forceRerender() {
      setKey((prevKey) => prevKey + 1);
    }
  }, [props.goToSlide]);
  const baseCarouselProps: React.ComponentProps<typeof AdaptiveCarousel> = {
    initialSlide: props.goToSlide?.slideNumber,
    key: `${props.mediaArray.all.map((m) => m.galleryId).join('-')}-${key}`,
    'aria-label': t('image-container-label'),
    onSlideChange: handleSlideChange,
    slidesToShow: 1,
    slidePicker: (
      <Thumbnails mediaArray={props.mediaArray} mpvId={props.mpvId} productKey={props.productKey} />
    ),
  };

  return (
    <Box id={GalleryId}>
      <Visible xs sm>
        <AdaptiveCarousel {...baseCarouselProps}>{renderCarouselSlides(true)}</AdaptiveCarousel>
        {props.zoomContainerId && (
          <ZoomDialog zoomData={zoomData} onClose={() => setZoomData(new ZoomedDisplayData())} />
        )}
      </Visible>

      <Visible md lg xl>
        <AdaptiveCarousel {...baseCarouselProps}>{renderCarouselSlides()}</AdaptiveCarousel>
      </Visible>
    </Box>
  );
};
